// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import Logger from "js-logger";
import { v4 as uuidv4 } from "uuid";
import { EventLogger } from "@/js/EventLogger";

export interface Config {
  DRAW_DETECTIONS: string;
  PROFILING: string;
  API_URL: string;
  API_START_ENDPOINT: string;
  API_VERIFY_ENDPOINT_PATTERN: string;
  API_FRAMES_ENDPOINT_PATTERN: string;
  API_VIDEO_ENDPOINT_PATTERN: string;
  IMAGE_JPG_QUALITY: string;
  STATE_AREA_DURATION_IN_SECONDS: string;
  STATE_NOSE_DURATION_IN_SECONDS: string;
  STATE_AREA_MAX_FRAMES_WITHOUT_FACE: string;
  STATE_NOSE_MAX_FRAMES_WITHOUT_FACE: string;
  STATE_FACE_UP_MAX_FRAMES_WITHOUT_FACE: string;
  MAX_FPS: string;
  FACE_AREA_TOLERANCE_PERCENT: string;
  FLIP_VIDEO: string;
  LANDMARK_INDEX: string;
  AMPLITUDE_API_KEY: string;
  RECORD_VIDEO_DURATION_IN_SECONDS: string;
  RECORD_VIDEO_BITRATE_IN_MBPS: string;
  STATE_AREA_MAX_FRAMES_REQUEST_FOR_FACE_TOO_LARGE: string;
}

export interface MediaResolution {
  width: number;
  height: number;
}

export interface MediaStreamInfo {
  mediaStream: MediaStream;
  actualHeight: number;
  actualWidth: number;
  elementScale: number;
}

export interface MediaRecorderInfo {
  recorder: MediaRecorder;
  recordedChunks: Blob[];
  mimeType: string;
}
export class Utils {
  private static KEY_PREFIX = "VUE_APP_";
  private static VIDEO_TYPES = ["webm", "mp4"];

  static loadConfig() {
    const envKeys = Object.keys(process.env);
    const map = new Map();
    envKeys.forEach(function(envKey) {
      const key = envKey.replace(Utils.KEY_PREFIX, "");
      const value = process.env[envKey] as string;
      map.set(key, value);
    });
    (window as any).config = Object.fromEntries(map);
  }

  static getConfig(): Config {
    return (window as any).config as Config;
  }

  static getConfigBooleanValue(configKey: string): boolean {
    return (
      // @ts-ignore
      Utils.getConfig()
        [configKey].trim()
        .toLowerCase() === "true"
    );
  }

  static async waitUntilCameraStreamIsClosed(cameraStream: MediaStream) {
    const wait = () => new Promise(res => setTimeout(res, 10));
    const firstTrack = cameraStream.getVideoTracks()[0];
    if (!firstTrack) return;
    while (firstTrack.readyState === "live") {
      await wait();
    }
  }
  static closeCameraStream(cameraStream: MediaStream) {
    const firstTrack = cameraStream.getVideoTracks()[0];
    if (firstTrack && typeof firstTrack.stop !== "undefined") {
      firstTrack.stop();
    }
  }
  static async closeCameraStreamAndWait(cameraStream: MediaStream) {
    Utils.closeCameraStream(cameraStream);
    await Utils.waitUntilCameraStreamIsClosed(cameraStream);
  }
  static isProfiling(): boolean {
    return Utils.getConfigBooleanValue("PROFILING");
  }

  static configureLogger() {
    Logger.useDefaults();
    const consoleHandler = Logger.createDefaultHandler();
    Logger.setHandler((messages, context) => {
      if (process.env.NODE_ENV != "production") {
        consoleHandler(messages, context);
      }
      if (context.level.name === "WARN") {
        (window as any).allLocalLogs = (window as any).allLocalLogs || [];
        (window as any).allLocalLogs.push(messages);
      }
    });
    (window as any).Logger = Logger;
  }

  static async getCurrentMediaResolution(cameraStrategy: string): Promise<MediaResolution> {
    const constraints: MediaStreamConstraints = {
      audio: false,
      video: {
        facingMode: "user"
      }
    };
    const mediaStrem = await Utils.requestCameraAsync(constraints);
    let width = mediaStrem.getVideoTracks()[0].getSettings().width || 0;
    let height = mediaStrem.getVideoTracks()[0].getSettings().height || 0;
    await this.closeCameraStreamAndWait(mediaStrem);
    if (cameraStrategy.includes("SwapResult")) {
      [width, height] = [height, width];
    }
    const currentResolution: MediaResolution = {
      width,
      height
    };
    Logger.log({ currentResolution });
    return currentResolution;
  }

  static async calculateCameraRequestDimension(
    expectWidth: number,
    expectHeight: number,
    cameraStrategy: string
  ): Promise<MediaResolution> {
    const { width, height } = await Utils.getCurrentMediaResolution(cameraStrategy);
    const expectRatio = expectWidth / expectHeight;
    if (height * expectRatio < width) {
      return {
        width: height * expectRatio,
        height
      };
    }
    return {
      width,
      height: width / expectRatio
    };
  }

  static async requestCameraAsync(constraints: MediaStreamConstraints): Promise<MediaStream> {
    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(resolve)
        .catch(reject);
    });
  }

  static async getVideoInputDevice(): Promise<MediaDeviceInfo | undefined> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.find(device => device.kind === "videoinput");
    } catch {
      return undefined;
    }
  }

  static async hasVideoInputDevice(): Promise<boolean> {
    return !!(await Utils.getVideoInputDevice());
  }

  static startMediaRecorder(): void {
    try {
      const mediaRecorderInfo: MediaRecorderInfo = (window as any).mediaRecorderInfo;
      if (!mediaRecorderInfo) {
        const supportedMimeType = this.supportedMedia();
        if (!supportedMimeType) throw "Cannot find any supported Mimetype for recorder";

        const mediaRecorderOption: any = {
          mimeType: `video/${supportedMimeType}`,
          videoBitsPerSecond: parseInt(Utils.getConfig().RECORD_VIDEO_BITRATE_IN_MBPS) * 1024 * 1024
        };
        const mediaRecorder = new MediaRecorder(Utils.getMediaStreamInfo().mediaStream, mediaRecorderOption);
        const recordedBlob: Blob[] = [];
        mediaRecorder.addEventListener("dataavailable", function(e: BlobEvent) {
          const videoMaxDuration = parseInt(Utils.getConfig().RECORD_VIDEO_DURATION_IN_SECONDS);
          if (mediaRecorder.state !== "recording" || recordedBlob.length >= videoMaxDuration) return;

          recordedBlob.push(e.data);
        });
        (window as any).mediaRecorderInfo = {
          recordedChunks: recordedBlob,
          recorder: mediaRecorder,
          mimeType: supportedMimeType
        };
        mediaRecorder.start(1000);
      } else if (mediaRecorderInfo.recorder.state !== "recording") {
        mediaRecorderInfo.recorder.start(1000);
      }
    } catch (error) {
      Logger.error("Cannot start media recorder");
      Logger.error(error);
      EventLogger.singleton.sendStartRecorderErrorEvent(error);
    }
  }

  static stopMediaRecorder(hasError = false): void {
    try {
      const recorderInfo = (window as any).mediaRecorderInfo;
      recorderInfo.recorder.stop();
      if (hasError) recorderInfo.recordedChunks.length = 0;
    } catch (error) {
      Logger.error("Cannot stop media recorder");
      Logger.error(error);
      EventLogger.singleton.sendStopRecorderErrorEvent(error);
    }
  }

  static resetMediaRecorder(): void {
    try {
      const recorderInfo = (window as any).mediaRecorderInfo;

      recorderInfo.recordedChunks.length = 0;
    } catch (error) {
      Logger.error("Cannot reset media recorder recorded chunks");
      Logger.error(error);
      EventLogger.singleton.sendResetRecorderErrorEvent(error);
    }
  }

  static supportedMedia(): string | undefined {
    const isSupported = MediaRecorder.isTypeSupported;
    return this.VIDEO_TYPES.find(type => {
      return isSupported(`video/${type}`);
    });
  }

  static async loadMediaStream(expectWidth: number, expectHeight: number, cameraStrategy: string): Promise<void> {
    const requestResolution = await Utils.calculateCameraRequestDimension(expectWidth, expectHeight, cameraStrategy);
    let width = requestResolution.width;
    let height = requestResolution.height;
    if (cameraStrategy.includes("SwapInit")) {
      [width, height] = [height, width];
    }
    const constraints: MediaStreamConstraints = {
      audio: false,
      video: {
        width,
        height,
        facingMode: "user"
      }
    };
    const mediaStream = await Utils.requestCameraAsync(constraints);
    let actualHeight = mediaStream.getVideoTracks()[0].getSettings().height || 0;
    let actualWidth = mediaStream.getVideoTracks()[0].getSettings().width || 0;
    if (cameraStrategy.includes("SwapResult")) {
      [actualWidth, actualHeight] = [actualHeight, actualWidth];
    }
    const mediaStreamInfo: MediaStreamInfo = {
      mediaStream: mediaStream,
      actualHeight,
      actualWidth,
      elementScale: expectWidth / requestResolution.width
    };
    Logger.info(`media info: actualHeight=${mediaStreamInfo.actualHeight} actualWidth=${mediaStreamInfo.actualWidth}`);
    (window as any).mediaStreamInfo = mediaStreamInfo;
    Utils.startMediaRecorder();
  }

  static getMediaStreamInfo(): MediaStreamInfo {
    return (window as any).mediaStreamInfo as MediaStreamInfo;
  }

  static getUserId(): string {
    let userId = window.localStorage.getItem("userId");
    if (userId === null) {
      userId = uuidv4();
      window.localStorage.setItem("userId", userId);
    }
    Logger.info(`userId=${userId}`);
    return userId;
  }

  static setRemiUsername(username: string): void {
    window.localStorage.setItem("userId", username);
  }

  static getChallengeType(): string {
    const challengeType = window.localStorage.getItem("challengeType");
    return challengeType || "nose";
  }
  static setChallengeType(challengeType: string): void {
    window.localStorage.setItem("challengeType", challengeType);
  }
  static setRemiUserId(userId: string): void {
    window.localStorage.setItem("remiUserId", userId);
  }
  static getRemiUserId(): number | null {
    try {
      const remiUserId = window.localStorage.getItem("remiUserId");
      return remiUserId ? parseInt(remiUserId) : null;
    } catch {
      return null;
    }
  }
}
